import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  CircularProgress,
  Fade,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Send as SendIcon,
  Report as ComplaintIcon,
  Lightbulb as SuggestionIcon,
  ThumbUp as CongratulationIcon,
} from '@mui/icons-material';
import { registerPQRS } from '../services/api_service';
import { useAlert } from '../AlertContext';

const ModernPQRS = () => {
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    type: 'PETICION',
    description: '',
  });
  const { showAlert } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const session = JSON.parse(localStorage.getItem('session') || '{}');

  const pqrsTypes = [
    {
      value: 'SUGERENCIA',
      label: 'Sugerencia',
      description: 'Proponer mejoras en nuestros servicios',
      icon: <SuggestionIcon />,
      color: '#4CAF50',
    },
    {
      value: 'QUEJA',
      label: 'Queja',
      description: 'Expresar inconformidad con un servicio',
      icon: <ComplaintIcon />,
      color: '#FF9800',
    },

    {
      value: 'FELICITACION',
      label: 'Felicitación',
      description: 'Reconocer un buen servicio recibido',
      icon: <CongratulationIcon />,
      color: '#00BCD4',
    },
  ];

  const onChange = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!state.type || !state.description.trim()) {
      showAlert('Por favor completa todos los campos', 'warning');
      return;
    }

    if (state.description.trim().length < 10) {
      showAlert('La descripción debe tener al menos 10 caracteres', 'warning');
      return;
    }

    setLoading(true);
    try {
      const body = {
        type: state.type,
        description: state.description.trim(),
        user_name: session?.user_name,
        user_document: session?.identification_number,
        user_id: session.user_id,
      };

      await registerPQRS(body);
      showAlert(
        'PQR registrado exitosamente. Nos pondremos en contacto para darle seguimiento a su caso',
        'success'
      );
      setState({ type: 'PETICION', description: '' });
    } catch (error) {
      showAlert('Error al enviar el formulario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = pqrsTypes.find((type) => type.value === state.type);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F8FFFE 0%, #FFFFFF 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="md">
        <Fade in={true} timeout={600}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background:
                    'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                  mb: 2,
                }}
              >
                <FeedbackIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 1,
                }}
              >
                PQR
              </Typography>
              {/*<Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 500, mx: 'auto' }}
              >
                Peticiones, Quejas, Reclamos, Sugerencias, Denuncias y
                Felicitaciones
              </Typography>*/}
            </Box>

            {/* Type Selection */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E0E0E0',
                p: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 3,
                }}
              >
                Selecciona el tipo de solicitud
              </Typography>

              <Grid container spacing={2}>
                {pqrsTypes.map((type) => (
                  <Grid item xs={12} sm={6} md={4} key={type.value}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border:
                          state.type === type.value
                            ? `2px solid ${type.color}`
                            : '1px solid #E0E0E0',
                        backgroundColor:
                          state.type === type.value
                            ? `${type.color}08`
                            : 'white',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${type.color}20`,
                          borderColor: type.color,
                        },
                        height: '100%',
                      }}
                      onClick={() => onChange('type', type.value)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2 }}>
                        <Box
                          sx={{
                            color: type.color,
                            mb: 1,
                            '& svg': { fontSize: 32 },
                          }}
                        >
                          {type.icon}
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            color:
                              state.type === type.value
                                ? type.color
                                : '#2C5530',
                            mb: 0.5,
                          }}
                        >
                          {type.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {type.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Form */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E0E0E0',
                p: 4,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {selectedType?.icon}
                <Box sx={{ ml: 1 }}>
                  Describe tu {selectedType?.label.toLowerCase()}
                </Box>
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Type Selector (Alternative) */}
                <FormControl fullWidth>
                  <InputLabel>Tipo de solicitud</InputLabel>
                  <Select
                    value={state.type}
                    label="Tipo de solicitud"
                    onChange={(e) => onChange('type', e.target.value)}
                  >
                    {pqrsTypes.map((type) => (
                      <MenuItem value={type.value} key={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ color: type.color, mr: 1 }}>
                            {type.icon}
                          </Box>
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Description */}
                <TextField
                  label="Describe tu caso en detalle"
                  fullWidth
                  multiline
                  rows={6}
                  value={state.description}
                  onChange={(e) => onChange('description', e.target.value)}
                  placeholder={`Describe tu ${selectedType?.label.toLowerCase()} de manera clara y detallada. Incluye fechas, nombres y cualquier información relevante que nos ayude a entender mejor tu situación.`}
                  helperText={`${state.description.length}/1000 caracteres (mínimo 10)`}
                  inputProps={{ maxLength: 1000 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />

                {/* User Info Display */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#F8FFF8',
                    borderRadius: 2,
                    border: '1px solid #E8F5E8',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    Información del solicitante:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Nombre:</strong>{' '}
                    {session?.user_name || 'No disponible'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Documento:</strong>{' '}
                    {session?.identification_number || 'No disponible'}
                  </Typography>
                </Box>

                {/* Submit Button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={
                    loading ||
                    !state.description.trim() ||
                    state.description.length < 10
                  }
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{
                    background: selectedType?.color
                      ? `linear-gradient(135deg, ${selectedType.color} 0%, ${selectedType.color}CC 100%)`
                      : 'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    boxShadow: `0 4px 12px ${selectedType?.color || '#94B754'}40`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 16px ${selectedType?.color || '#94B754'}50`,
                    },
                    '&:disabled': {
                      background: '#ccc',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {loading ? 'Enviando...' : `Enviar ${selectedType?.label}`}
                </Button>
              </Box>
            </Paper>

            {/* Info Box */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid #E3F2FD',
                backgroundColor: '#F8FBFF',
                p: 3,
                mt: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                📞 <strong>Tiempo de respuesta:</strong> 5 días hábiles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nos pondremos en contacto contigo para darle seguimiento a tu
                solicitud
              </Typography>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ModernPQRS;
